import {
  AREA_TYPE_LABELS,
  COMPANY_LABELS,
  formatNumber,
  slugify,
  type AreaDto,
  type AreaSourceDto,
  type DiscoveredSourceDto,
  type UserDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, Globe, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

const MONTH_LABELS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'mai',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'des',
];

/**
 * Admin → Områder: full create/edit for product areas (07 — 2.2.3). Areas are
 * never deleted — an area with content must not disappear — so deactivation
 * plays the role deletion does for users.
 */
export function AreasAdmin() {
  const queryClient = useQueryClient();
  const { data: areas = [], isLoading } = useQuery({
    queryKey: ['areas', 'admin'],
    queryFn: () => api.get<AreaDto[]>('/areas?includeInactive=true'),
  });
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserDto[]>('/users'),
  });

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const setOwner = useMutation({
    mutationFn: ({ areaId, ownerUserId }: { areaId: string; ownerUserId: string }) =>
      api.patch(`/areas/${areaId}`, { ownerUserId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['areas'] }),
  });

  const addMember = useMutation({
    mutationFn: ({ areaId, userId }: { areaId: string; userId: string }) =>
      api.post(`/areas/${areaId}/members`, { userId, role: 'REMINDER_LIST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['areas'] }),
  });

  const removeMember = useMutation({
    mutationFn: ({ areaId, userId }: { areaId: string; userId: string }) =>
      api.delete(`/areas/${areaId}/members/${userId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['areas'] }),
  });

  const setActive = useMutation({
    mutationFn: ({ areaId, isActive }: { areaId: string; isActive: boolean }) =>
      api.patch(`/areas/${areaId}`, { isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['areas'] }),
  });

  if (isLoading) return <SkeletonPanel lines={6} />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-ink-soft text-sm">
          {formatNumber(areas.filter((a) => a.isActive).length)} aktive områder.
        </p>
        <Button
          size="sm"
          variant={creating ? 'secondary' : 'primary'}
          icon={<Plus size={13} aria-hidden />}
          onClick={() => {
            setEditingId(null);
            setCreating((v) => !v);
          }}
        >
          Nytt område
        </Button>
      </div>

      {creating && (
        <GlassPanel>
          <h2 className="font-display text-ink mb-3 flex items-center gap-2 text-base">
            <Building2 size={15} className="text-accent" aria-hidden />
            Nytt område
          </h2>
          <AreaForm
            users={users}
            onDone={() => setCreating(false)}
            onCancel={() => setCreating(false)}
          />
        </GlassPanel>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {areas.map((area) => {
          const reminders = area.members?.filter((m) => m.role === 'REMINDER_LIST') ?? [];
          const available = users.filter(
            (u) => u.id !== area.ownerUserId && !reminders.some((m) => m.userId === u.id),
          );
          const editing = editingId === area.id;

          return (
            <GlassPanel
              key={area.id}
              accentColor={area.color}
              className={cn(!area.isActive && !editing && 'opacity-60')}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-display text-ink flex items-center gap-2 text-base">
                    <span className="truncate">{area.name}</span>
                    {!area.isActive && (
                      <span className="border-ink-soft/25 text-ink-soft shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-normal">
                        Deaktivert
                      </span>
                    )}
                  </h2>
                  <p className="text-ink-soft mt-0.5 text-[11px]">
                    {AREA_TYPE_LABELS[area.type]} · {COMPANY_LABELS[area.company]}
                    {area.cadenceDays !== null &&
                      ` · rytme: hver ${area.cadenceDays}. dag${
                        area.peakMonths && area.peakMonths.length > 0
                          ? ` (${area.peakMonths.map((m) => MONTH_LABELS[m - 1]).join(', ')}: hver ${
                              area.peakCadenceDays ?? area.cadenceDays
                            }. dag)`
                          : ''
                      }`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {area.stats && !editing && (
                    <div className="flex gap-2 text-center">
                      <Stat label="Aktive" value={area.stats.active} />
                      <Stat label="Forsinket" value={area.stats.overdue} tone="danger" />
                      <Stat
                        label="Publisert"
                        value={area.stats.publishedLast30Days}
                        tone="success"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`Rediger ${area.name}`}
                    title="Rediger området"
                    onClick={() => {
                      setCreating(false);
                      setEditingId(editing ? null : area.id);
                    }}
                    className={cn(
                      'shrink-0 rounded p-1.5 transition',
                      editing ? 'text-accent' : 'text-ink-soft hover:text-ink',
                    )}
                  >
                    <Pencil size={13} aria-hidden />
                  </button>
                </div>
              </div>

              {editing ? (
                <div className="mt-3">
                  <AreaForm
                    area={area}
                    users={users}
                    onDone={() => setEditingId(null)}
                    onCancel={() => setEditingId(null)}
                  />
                  <SourcesEditor area={area} />
                  <div className="border-hairline mt-3 flex items-center justify-between gap-3 border-t pt-3">
                    <p className="text-ink-soft text-[11px] leading-relaxed">
                      Områder slettes aldri — innholdet skal aldri miste hjemmet sitt. Deaktivering
                      skjuler området fra planlegging og filtre.
                    </p>
                    <Button
                      size="sm"
                      variant={area.isActive ? 'ghost' : 'secondary'}
                      loading={setActive.isPending && setActive.variables?.areaId === area.id}
                      onClick={() =>
                        setActive.mutate({ areaId: area.id, isActive: !area.isActive })
                      }
                    >
                      {area.isActive ? 'Deaktiver' : 'Aktiver igjen'}
                    </Button>
                  </div>
                  {setActive.isError && (
                    <p className="text-danger mt-2 text-xs">{(setActive.error as Error).message}</p>
                  )}
                </div>
              ) : (
                <>
                  {area.valueProposition && (
                    <p className="text-ink-soft mt-2 text-sm leading-relaxed">
                      {area.valueProposition}
                    </p>
                  )}

                  <div className="mt-4">
                    <label className="label-micro mb-1.5 block" htmlFor={`owner-${area.id}`}>
                      Produktansvarlig
                    </label>
                    <select
                      id={`owner-${area.id}`}
                      value={area.ownerUserId}
                      onChange={(e) =>
                        setOwner.mutate({ areaId: area.id, ownerUserId: e.target.value })
                      }
                      className="border-hairline bg-base/40 text-ink h-9 w-full rounded-lg border px-2.5 text-sm"
                    >
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-3">
                    <p className="label-micro mb-1.5">Purreliste</p>
                    <div className="flex flex-wrap gap-1.5">
                      {reminders.length === 0 && (
                        <span className="text-ink-soft text-[11px]">Ingen på purrelisten.</span>
                      )}
                      {reminders.map((member) => (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() =>
                            removeMember.mutate({ areaId: area.id, userId: member.userId })
                          }
                          title="Klikk for å fjerne"
                          className="border-hairline bg-raised/60 text-ink-soft hover:border-danger/40 hover:text-danger rounded-full border px-2.5 py-1 text-[11px] transition"
                        >
                          {member.user?.name ?? 'Ukjent'} ×
                        </button>
                      ))}
                    </div>

                    {available.length > 0 && (
                      <select
                        value=""
                        aria-label={`Legg til på purrelisten for ${area.name}`}
                        onChange={(e) =>
                          e.target.value &&
                          addMember.mutate({ areaId: area.id, userId: e.target.value })
                        }
                        className="border-hairline bg-base/40 text-ink-soft mt-2 h-8 rounded-lg border px-2 text-[11px]"
                      >
                        <option value="">+ Legg til på purrelisten …</option>
                        {available.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </>
              )}
            </GlassPanel>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One form for both create and edit. The slug follows the name until someone
 * touches it (create only — after creation it is the area's identity and
 * stays put).
 */
function AreaForm({
  area,
  users,
  onDone,
  onCancel,
}: {
  area?: AreaDto;
  users: UserDto[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: area?.name ?? '',
    slug: area?.slug ?? '',
    slugTouched: Boolean(area),
    type: area?.type ?? 'PRODUCT',
    company: area?.company ?? 'PROKOM',
    color: area?.color ?? '#38BDF8',
    valueProposition: area?.valueProposition ?? '',
    targetAudience: area?.targetAudience ?? '',
    ownerUserId: area?.ownerUserId ?? users[0]?.id ?? '',
    linkedUserId: area?.linkedUserId ?? '',
    cadenceDays: area?.cadenceDays?.toString() ?? '',
    peakMonths: area?.peakMonths ?? [],
    peakCadenceDays: area?.peakCadenceDays?.toString() ?? '',
    lowMonths: area?.lowMonths ?? [],
    lowCadenceDays: area?.lowCadenceDays?.toString() ?? '',
  });

  const save = useMutation({
    mutationFn: () => {
      const cadenceDays = Number.parseInt(form.cadenceDays, 10);
      const peakCadenceDays = Number.parseInt(form.peakCadenceDays, 10);
      const lowCadenceDays = Number.parseInt(form.lowCadenceDays, 10);
      const body = {
        name: form.name.trim(),
        type: form.type,
        company: form.company,
        color: form.color,
        valueProposition: form.valueProposition.trim() || null,
        targetAudience: form.targetAudience.trim() || null,
        ownerUserId: form.ownerUserId,
        linkedUserId:
          form.type === 'EMPLOYEE_PROFILE' && form.linkedUserId ? form.linkedUserId : null,
        cadenceDays: Number.isFinite(cadenceDays) && cadenceDays > 0 ? cadenceDays : null,
        peakMonths: form.peakMonths.length > 0 ? [...form.peakMonths].sort((a, b) => a - b) : null,
        peakCadenceDays:
          Number.isFinite(peakCadenceDays) && peakCadenceDays > 0 ? peakCadenceDays : null,
        lowMonths: form.lowMonths.length > 0 ? [...form.lowMonths].sort((a, b) => a - b) : null,
        lowCadenceDays:
          Number.isFinite(lowCadenceDays) && lowCadenceDays > 0 ? lowCadenceDays : null,
      };
      return area
        ? api.patch(`/areas/${area.id}`, body)
        : api.post('/areas', { ...body, slug: form.slug });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['areas'] });
      onDone();
    },
  });

  const valid = form.name.trim().length >= 2 && form.ownerUserId && (area || form.slug.length >= 2);

  const input = 'border-hairline bg-base/50 text-ink h-9 w-full rounded-lg border px-2.5 text-sm';

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="label-micro mb-1.5 block" htmlFor={`area-name-${area?.id ?? 'new'}`}>
          Navn
        </label>
        <input
          id={`area-name-${area?.id ?? 'new'}`}
          value={form.name}
          onChange={(e) =>
            setForm({
              ...form,
              name: e.target.value,
              slug: form.slugTouched ? form.slug : slugify(e.target.value),
            })
          }
          placeholder="F.eks. BookUp"
          className={input}
        />
      </div>

      {!area && (
        <div>
          <label className="label-micro mb-1.5 block" htmlFor="area-slug-new">
            Slug (adresse og filtre)
          </label>
          <input
            id="area-slug-new"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value), slugTouched: true })}
            placeholder="bookup"
            className={cn(input, 'font-mono text-[13px]')}
          />
        </div>
      )}

      <div>
        <label className="label-micro mb-1.5 block" htmlFor={`area-type-${area?.id ?? 'new'}`}>
          Type
        </label>
        <select
          id={`area-type-${area?.id ?? 'new'}`}
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value as AreaDto['type'] })}
          className={input}
        >
          {Object.entries(AREA_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="label-micro mb-1.5 block" htmlFor={`area-company-${area?.id ?? 'new'}`}>
          Selskap
        </label>
        <select
          id={`area-company-${area?.id ?? 'new'}`}
          value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value as AreaDto['company'] })}
          className={input}
        >
          {Object.entries(COMPANY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="label-micro mb-1.5 block" htmlFor={`area-owner-${area?.id ?? 'new'}`}>
          Produktansvarlig
        </label>
        <select
          id={`area-owner-${area?.id ?? 'new'}`}
          value={form.ownerUserId}
          onChange={(e) => setForm({ ...form, ownerUserId: e.target.value })}
          className={input}
        >
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="label-micro mb-1.5 block" htmlFor={`area-color-${area?.id ?? 'new'}`}>
          Farge
        </label>
        <div className="flex items-center gap-2">
          <input
            id={`area-color-${area?.id ?? 'new'}`}
            type="color"
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            className="border-hairline bg-base/50 h-9 w-14 cursor-pointer rounded-lg border p-1"
          />
          <span className="text-ink-soft font-mono text-[11px] uppercase">{form.color}</span>
        </div>
      </div>

      {form.type === 'EMPLOYEE_PROFILE' && (
        <div>
          <label className="label-micro mb-1.5 block" htmlFor={`area-linked-${area?.id ?? 'new'}`}>
            Knyttet ansatt
          </label>
          <select
            id={`area-linked-${area?.id ?? 'new'}`}
            value={form.linkedUserId}
            onChange={(e) => setForm({ ...form, linkedUserId: e.target.value })}
            className={input}
          >
            <option value="">Ingen</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="border-hairline bg-base/30 rounded-lg border p-3 sm:col-span-2">
        <p className="label-micro mb-2">Rytme (07 — aktivitetsplan)</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="text-ink-soft" htmlFor={`area-cadence-${area?.id ?? 'new'}`}>
            Minst én sak hver
          </label>
          <input
            id={`area-cadence-${area?.id ?? 'new'}`}
            type="number"
            min={1}
            max={365}
            value={form.cadenceDays}
            onChange={(e) => setForm({ ...form, cadenceDays: e.target.value })}
            placeholder="–"
            className="border-hairline bg-base/50 text-ink h-8 w-16 rounded-lg border px-2 text-center text-sm"
          />
          <span className="text-ink-soft">
            dag. Tomt betyr at planleggerens felles standard gjelder.
          </span>
        </div>

        <p className="label-micro mb-1.5 mt-3">Høysesong — måneder med høyere behov</p>
        <div className="flex flex-wrap gap-1">
          {MONTH_LABELS.map((label, index) => {
            const month = index + 1;
            const active = form.peakMonths.includes(month);
            return (
              <button
                key={month}
                type="button"
                aria-pressed={active}
                onClick={() =>
                  setForm({
                    ...form,
                    peakMonths: active
                      ? form.peakMonths.filter((m) => m !== month)
                      : [...form.peakMonths, month],
                  })
                }
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px] transition',
                  active
                    ? 'border-accent/40 bg-accent/15 text-accent'
                    : 'border-hairline bg-base/40 text-ink-soft hover:text-ink',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {form.peakMonths.length > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-sm">
            <label className="text-ink-soft" htmlFor={`area-peak-cadence-${area?.id ?? 'new'}`}>
              I høysesong: minst én sak hver
            </label>
            <input
              id={`area-peak-cadence-${area?.id ?? 'new'}`}
              type="number"
              min={1}
              max={365}
              value={form.peakCadenceDays}
              onChange={(e) => setForm({ ...form, peakCadenceDays: e.target.value })}
              placeholder={form.cadenceDays || '–'}
              className="border-hairline bg-base/50 text-ink h-8 w-16 rounded-lg border px-2 text-center text-sm"
            />
            <span className="text-ink-soft">dag.</span>
          </div>
        )}

        <p className="label-micro mb-1.5 mt-3">Lavsesong — roligere måneder</p>
        <div className="flex flex-wrap gap-1">
          {MONTH_LABELS.map((label, index) => {
            const month = index + 1;
            const active = form.lowMonths.includes(month);
            return (
              <button
                key={month}
                type="button"
                aria-pressed={active}
                onClick={() =>
                  setForm({
                    ...form,
                    lowMonths: active
                      ? form.lowMonths.filter((m) => m !== month)
                      : [...form.lowMonths, month],
                  })
                }
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px] transition',
                  active
                    ? 'border-ink-soft/50 bg-ink-soft/15 text-ink'
                    : 'border-hairline bg-base/40 text-ink-soft hover:text-ink',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {form.lowMonths.length > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-sm">
            <label className="text-ink-soft" htmlFor={`area-low-cadence-${area?.id ?? 'new'}`}>
              I lavsesong: minst én sak hver
            </label>
            <input
              id={`area-low-cadence-${area?.id ?? 'new'}`}
              type="number"
              min={1}
              max={365}
              value={form.lowCadenceDays}
              onChange={(e) => setForm({ ...form, lowCadenceDays: e.target.value })}
              placeholder={form.cadenceDays || '–'}
              className="border-hairline bg-base/50 text-ink h-8 w-16 rounded-lg border px-2 text-center text-sm"
            />
            <span className="text-ink-soft">dag.</span>
          </div>
        )}
      </div>

      <div className="sm:col-span-2">
        <label className="label-micro mb-1.5 block" htmlFor={`area-value-${area?.id ?? 'new'}`}>
          Verdiløfte
        </label>
        <textarea
          id={`area-value-${area?.id ?? 'new'}`}
          value={form.valueProposition}
          onChange={(e) => setForm({ ...form, valueProposition: e.target.value })}
          rows={2}
          placeholder="Hva lover dette området markedet?"
          className="border-hairline bg-base/50 text-ink w-full rounded-lg border px-2.5 py-2 text-sm"
        />
      </div>

      <div className="sm:col-span-2">
        <label className="label-micro mb-1.5 block" htmlFor={`area-audience-${area?.id ?? 'new'}`}>
          Målgruppe
        </label>
        <input
          id={`area-audience-${area?.id ?? 'new'}`}
          value={form.targetAudience}
          onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
          placeholder="F.eks. kommuner, banker, forsikring"
          className={input}
        />
      </div>

      <div className="flex items-center gap-2 sm:col-span-2">
        <Button
          size="sm"
          variant="primary"
          loading={save.isPending}
          disabled={!valid}
          onClick={() => save.mutate()}
        >
          {area ? 'Lagre' : 'Opprett område'}
        </Button>
        <Button size="sm" onClick={onCancel}>
          Avbryt
        </Button>
      </div>
      {save.isError && (
        <p className="text-danger text-xs sm:col-span-2">{(save.error as Error).message}</p>
      )}
    </div>
  );
}

/**
 * Source governance for the scout (07 — kildestyring): the curated watch-list,
 * the switch that lets Nova search beyond it, and the box of sources Nova has
 * used on its own — each promotable into the list or dismissed.
 */
function SourcesEditor({ area }: { area: AreaDto }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  const { data: sources = [] } = useQuery({
    queryKey: ['area-sources', area.id],
    queryFn: () => api.get<AreaSourceDto[]>(`/areas/${area.id}/sources`),
  });
  const { data: discovered = [] } = useQuery({
    queryKey: ['discovered-sources', area.id],
    queryFn: () =>
      api.get<DiscoveredSourceDto[]>(`/areas/${area.id}/discovered-sources?status=SUGGESTED`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['area-sources', area.id] });
    void queryClient.invalidateQueries({ queryKey: ['discovered-sources', area.id] });
  };

  const toggleExternal = useMutation({
    mutationFn: () =>
      api.patch(`/areas/${area.id}`, { allowExternalSources: !area.allowExternalSources }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['areas'] }),
  });

  const addSource = useMutation({
    mutationFn: () =>
      api.post(`/areas/${area.id}/sources`, { label: label.trim(), url: url.trim() }),
    onSuccess: () => {
      setLabel('');
      setUrl('');
      invalidate();
    },
  });

  const removeSource = useMutation({
    mutationFn: (sourceId: string) => api.delete(`/areas/${area.id}/sources/${sourceId}`),
    onSuccess: invalidate,
  });

  const promote = useMutation({
    mutationFn: (id: string) =>
      api.post(`/areas/${area.id}/discovered-sources/${id}/promote`),
    onSuccess: invalidate,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      api.post(`/areas/${area.id}/discovered-sources/${id}/dismiss`),
    onSuccess: invalidate,
  });

  const canAdd = label.trim().length > 0 && /^https?:\/\//i.test(url.trim());

  return (
    <div className="border-hairline bg-base/30 mt-3 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Globe size={14} className="text-accent" aria-hidden />
        <p className="label-micro">Kilder Nova overvåker</p>
      </div>

      <div className="mt-2 space-y-1.5">
        {sources.length === 0 && (
          <p className="text-ink-soft text-[11px]">
            Ingen faste kilder ennå. Legg til de nettstedene Nova skal følge for dette området.
          </p>
        )}
        {sources.map((source) => (
          <div
            key={source.id}
            className="border-hairline bg-panel/40 flex items-center gap-2 rounded-lg border px-2.5 py-1.5"
          >
            <div className="min-w-0 flex-1">
              <p className="text-ink truncate text-xs font-medium">{source.label}</p>
              <p className="text-ink-soft truncate text-[11px]">{source.url}</p>
            </div>
            <button
              type="button"
              aria-label={`Fjern ${source.label}`}
              onClick={() => removeSource.mutate(source.id)}
              className="text-ink-soft hover:text-danger shrink-0 rounded p-1 transition"
            >
              <Trash2 size={13} aria-hidden />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Navn, f.eks. Digdir"
          className="border-hairline bg-base/50 text-ink h-8 min-w-[8rem] flex-1 rounded-lg border px-2 text-xs"
        />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://digdir.no"
          className="border-hairline bg-base/50 text-ink h-8 min-w-[10rem] flex-[2] rounded-lg border px-2 text-xs"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus size={12} aria-hidden />}
          disabled={!canAdd}
          loading={addSource.isPending}
          onClick={() => addSource.mutate()}
        >
          Legg til
        </Button>
      </div>

      <button
        type="button"
        onClick={() => toggleExternal.mutate()}
        disabled={toggleExternal.isPending}
        className="mt-3 flex w-full items-center gap-2.5 text-left"
      >
        <span
          className={cn(
            'relative h-5 w-9 shrink-0 rounded-full transition',
            area.allowExternalSources ? 'bg-accent/70' : 'bg-raised',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
              area.allowExternalSources ? 'left-4' : 'left-0.5',
            )}
          />
        </span>
        <span className="text-ink-soft text-[11px] leading-snug">
          {area.allowExternalSources
            ? 'Nova kan søke utenfor listen. Kilder den bruker havner som forslag under.'
            : 'Nova holder seg til listen over. Skru på for å la den søke bredere.'}
        </span>
      </button>

      {discovered.length > 0 && (
        <div className="border-hairline mt-3 border-t pt-3">
          <div className="flex items-center gap-2">
            <Sparkles size={13} className="text-accent" aria-hidden />
            <p className="label-micro">Kilder Nova har brukt — forslag til listen</p>
          </div>
          <div className="mt-2 space-y-1.5">
            {discovered.map((source) => (
              <div
                key={source.id}
                className="border-hairline bg-panel/40 flex items-center gap-2 rounded-lg border px-2.5 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-ink truncate text-xs">{source.label ?? source.url}</p>
                  <p className="text-ink-soft truncate text-[11px]">
                    {source.url} · brukt {formatNumber(source.timesUsed)} ganger
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Legg ${source.label ?? source.url} til i listen`}
                  title="Legg til i listen"
                  onClick={() => promote.mutate(source.id)}
                  className="text-ink-soft hover:text-success shrink-0 rounded p-1 transition"
                >
                  <Check size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={`Avvis ${source.label ?? source.url}`}
                  title="Avvis"
                  onClick={() => dismiss.mutate(source.id)}
                  className="text-ink-soft hover:text-danger shrink-0 rounded p-1 transition"
                >
                  <Trash2 size={13} aria-hidden />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'danger' | 'success';
}) {
  return (
    <div className="border-hairline bg-base/30 rounded-lg border px-2 py-1">
      <p
        className={cn(
          'num-tabular font-display text-sm font-bold',
          tone === 'danger' ? 'text-danger' : tone === 'success' ? 'text-success' : 'text-ink',
        )}
      >
        {formatNumber(value)}
      </p>
      <p className="label-micro">{label}</p>
    </div>
  );
}
